'use es6';

import * as DSAssetFamilies from 'customer-data-filters/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import * as ObjectTypeIds from 'customer-data-objects/constants/ObjectTypeIds';
import { EMAIL_CAMPAIGN_URL, SITE_CONTENT } from 'reference-resolvers/constants/ReferenceObjectTypes';
import { List, Map as ImmutableMap, OrderedMap, Set as ImmutableSet } from 'immutable';
import { isCustomBehavioralEventObjectTypeId, isUnifiedEventObjectTypeId, ObjectTypeIdRegex } from 'customer-data-filters/filterQueryFormat/ObjectTypeId';
import { PRIVACY_CONSENT } from 'customer-data-filters/converters/listSegClassic/ListSegConstants';
import { __ANY_LINK, __ANY_PAGE } from 'customer-data-filters/converters/listSegClassic/ListSegConstants';
import { filterFamilySupportsDynamic, makeDynamicFilterFamily, parseDynamicFilterFamilyId, testDynamicFilterFamily } from '../../filterQueryFormat/DynamicFilterFamilies';
import { getFieldDefinitions as getListSegClassicFieldDefinitions } from 'customer-data-filters/converters/listSegClassic/ListSegClassicFieldDefinitions';
import { getSpecialField, isSpecialField } from 'customer-data-filters/converters/listSegClassic/listSegFields';
import { getVisibleFields } from 'customer-data-filters/converters/listSegClassic/listSegFields';
import DSFieldRecord from 'customer-data-filters/filterQueryFormat/DSFieldRecord/DSFieldRecord';
import FieldResolverPrivacyConsent from 'customer-data-filters/filterQueryFormat/knownOptionResolvers/FieldResolverPrivacyConsent';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import PropertyGroupRecord from 'customer-data-objects/property/PropertyGroupRecord';
import PropertyOptionRecord from 'customer-data-objects/property/PropertyOptionRecord';
import always from 'transmute/always';
import getIn from 'transmute/getIn';
import once from 'transmute/once';
import unescapedText from 'I18n/utils/unescapedText';
import update from 'transmute/update';
import { jsx as _jsx } from "react/jsx-runtime";
const getFetchedFailedMessage = once(() => /*#__PURE__*/_jsx(FormattedMessage, {
  message: "customerDataFilters.MissingField.fetchFailed"
}));
export const isFieldGrouped = assetFamily => {
  if (isUnifiedEventObjectTypeId(assetFamily)) {
    return false;
  }
  if (isCustomBehavioralEventObjectTypeId(assetFamily)) {
    return false;
  }
  return ObjectTypeIdRegex.test(assetFamily) || assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION;
};
const getSpecialFieldGroups = assetFamily => {
  if (assetFamily === DSAssetFamilies.CONTACT_PROPERTY) {
    return OrderedMap({
      privacy: DSFieldRecord({
        label: I18n.text('customerDataFilters.FilterPropertyGroupTranslator.privacy'),
        name: 'privacy',
        displayOrder: 100
      })
    });
  }
  return OrderedMap();
};
export const onMount = (props, requiredIdsByAssetFamily) => {
  const {
    fetchMore
  } = props;
  // fetch required fields for integrations
  fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.INTEGRATION
    }
  }).then(results => {
    const activeIntegrationIds = requiredIdsByAssetFamily.get(DSAssetFamilies.INTEGRATION, ImmutableSet()).keySeq().map(value => parseDynamicFilterFamilyId(DSAssetFamilies.INTEGRATION, value)).filter(id => results.get(id)).toArray();
    if (activeIntegrationIds.length > 0) {
      fetchMore({
        variables: {
          assetFamily: DSAssetFamilies.INTEGRATION_PROPERTY,
          ids: activeIntegrationIds
        }
      });
    }
  });

  // fetch all infra family data
  fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.SURVEY_MONKEY_SETTING,
      batchUpdate: true
    }
  });
  fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.GOTO_WEBINAR_SETTING,
      batchUpdate: true
    }
  });
};
export const getField = (assetFamily, id, defaultField, props) => {
  const {
    data
  } = props;
  if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, assetFamily)) {
    const integrations = data.get(DSAssetFamilies.INTEGRATION);
    const integrationId = parseDynamicFilterFamilyId(DSAssetFamilies.INTEGRATION, assetFamily);
    const field = isSpecialField(id, assetFamily) ? getSpecialField(id, assetFamily) : data.getIn([DSAssetFamilies.INTEGRATION_PROPERTY, integrationId, id]);
    if (integrations && field) {
      return integrations.has(integrationId) ? field : defaultField.set('errorMessage', getFetchedFailedMessage());
    }
    return defaultField;
  }
  if (filterFamilySupportsDynamic(assetFamily)) {
    return data.getIn([makeDynamicFilterFamily(assetFamily, props.baseFilterFamily), `${id}`]);
  }
  if (isSpecialField(id, assetFamily)) {
    return getSpecialField(id, assetFamily);
  }
  return undefined;
};
export const getFieldDefinitions = filterFamily => getIn([filterFamily], getListSegClassicFieldDefinitions());
export const getFieldGroups = (assetFamily, props) => {
  const {
    data,
    hasScope
  } = props;
  if (isFieldGrouped(assetFamily)) {
    const fields = data.get(assetFamily);
    let groups = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? data.get(DSAssetFamilies.SURVEY_MONKEY_SURVEY) : data.getIn([DSAssetFamilies.PROPERTY_GROUP, assetFamily]);
    if (groups && fields) {
      groups = groups.merge(getSpecialFieldGroups(assetFamily));
      const scopes = {
        'crm-views-all': hasScope('crm-views-all')
      };
      const filteredFields = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? fields : getVisibleFields(fields, assetFamily, scopes);
      return filteredFields.reduce((acc, entry) => {
        const key = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? getIn(['metadata', 'surveyId'], entry) : getIn(['wrappedObject', 'groupName'], entry);
        const displayName = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? groups.getIn([key, 'label']) : groups.getIn([key, 'label']) || groups.getIn([key, 'name']);
        const fieldGroup = PropertyGroupRecord({
          displayOrder: groups.getIn([key, 'displayOrder']),
          displayName,
          name: groups.getIn([key, 'name'])
        });
        return update(key, (group = fieldGroup) => update('properties', properties => properties.push(entry), group), acc);
      }, OrderedMap()).filter(propertyGroupRecord => propertyGroupRecord.displayName).sort((a, b) => a.displayOrder - b.displayOrder);
    }
  }
  return undefined;
};
export const getFields = (assetFamily, props) => {
  if (assetFamily) {
    const {
      data,
      hasScope
    } = props;
    if (isFieldGrouped(assetFamily)) {
      return undefined;
    }
    let fields = data.get(assetFamily);
    if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, assetFamily)) {
      const integrationId = parseDynamicFilterFamilyId(DSAssetFamilies.INTEGRATION, assetFamily);
      fields = data.getIn([DSAssetFamilies.INTEGRATION_PROPERTY, integrationId]);
    }
    const scopes = {
      'crm-views-all': hasScope('crm-views-all')
    };
    const visibleFields = getVisibleFields(fields, assetFamily, scopes);
    if (visibleFields && visibleFields.size === 0 && fields && fields.size > 0) {
      return undefined;
    }
    return visibleFields;
  }
  return undefined;
};
export const getSpecialFieldOptions = always({
  [EMAIL_CAMPAIGN_URL]: () => List.of(PropertyOptionRecord({
    label: unescapedText('customerDataFilters.FilterEditor.specialOptionValues.anyLink'),
    value: __ANY_LINK
  })),
  [SITE_CONTENT]: () => List.of(PropertyOptionRecord({
    label: unescapedText('customerDataFilters.FilterEditor.specialOptionValues.anyPage'),
    value: __ANY_PAGE
  }))
});
export const getReferenceResolvers = always(ImmutableMap({
  [PRIVACY_CONSENT]: FieldResolverPrivacyConsent
}));
export const getEnabledStandardObjects = always(ImmutableSet(ObjectTypeIds.CONTACT_TYPE_ID, ObjectTypeIds.COMPANY_TYPE_ID, ObjectTypeIds.DEAL_TYPE_ID, ObjectTypeIds.ENGAGEMENT_TYPE_ID, ObjectTypeIds.LINE_ITEM_TYPE_ID, ObjectTypeIds.PRODUCT_TYPE_ID, ObjectTypeIds.QUOTE_TYPE_ID, ObjectTypeIds.SUBSCRIPTION_TYPE_ID, ObjectTypeIds.TICKET_TYPE_ID));